/**
 * Handler-specific type definitions
 */

import type { ToolHandler, ToolResult, ToolExecutionContext, FieldChange } from './tool.types';

/**
 * Edit preview returned by edit handlers
 */
export interface EditPreview extends ToolResult {
  type: 'preview';
  entity: string;
  id: string;
  changes: Record<string, FieldChange>;
  confirmationToken: string;
  preview: Record<string, any>;
}

/**
 * Search results returned by search handlers
 */
export interface SearchResults<T = any> extends ToolResult {
  type: 'success';
  data: {
    query: string;
    results: T[];
    total: number;
    hasMore: boolean;
  };
}

/**
 * Query handler interface
 */
export interface QueryHandler<T = any> extends ToolHandler {
  id: string;
  schema: any;
  execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<ToolResult>;
  validate?(params: unknown): Promise<void>;
  formatResult?(data: T): Promise<ToolResult>;
}

/**
 * Edit handler interface
 */
export interface EditHandler<T = any> extends ToolHandler {
  id: string;
  schema: any;
  execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<ToolResult>;
  validate?(params: unknown): Promise<void>;
  generatePreview?(current: T, updates: Record<string, any>): Promise<EditPreview>;
}

/**
 * Search handler interface
 */
export interface SearchHandler<T = any> extends ToolHandler {
  id: string;
  schema: any;
  execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<ToolResult>;
}

/**
 * Base handler class for queries
 */
export abstract class BaseQueryHandler<T = any> implements QueryHandler<T> {
  abstract id: string;
  abstract schema: any;

  abstract execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<ToolResult>;

  async validate(params: unknown): Promise<void> {
    // Override in subclasses
  }

  async formatResult(data: T): Promise<ToolResult> {
    return {
      type: 'success',
      data,
    };
  }
}

/**
 * Base handler class for edits
 */
export abstract class BaseEditHandler<T = any> implements EditHandler<T> {
  abstract id: string;
  abstract schema: any;

  abstract execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<ToolResult>;

  async validate(params: unknown): Promise<void> {
    // Override in subclasses
  }

  async generatePreview(current: T, updates: Record<string, any>): Promise<EditPreview> {
    // Override in subclasses
    throw new Error('generatePreview must be implemented');
  }
}
